import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, catchError, firstValueFrom, map, Subject, take, tap, throwError } from 'rxjs';
import { ApiService, USER_ROLES } from '../../services/api.service';
import { SetUserRole, SetUserRoleSuccess } from '../user/user.actions';
import { UserProxy } from '../user/user.proxy';
import { selectUserEmail } from '../user/user.store';
import { CheckServerSteps, GetGeneralInfo, GetHardSkills, GetJobTitles, GetSkillsList, GetSoftSkills, InitSteps, SetCurrentStep, SetGeneralInfo, SetHardSkills, SetSoftSkills, SetSoftSkillsSuccess, SetTalentSeniority, SetTalentSeniorityFailure, SetTalentSenioritySuccess, StepValid } from './onboarding.actions';
import { OnboardingState, selectHmGeneral, selectCurrentSteps, selectPartnerGeneral, selectStepsState, selectTalentGeneral, selectTalentHardSkills, selectTalentSoftSkills, selectJobTitles, selectCurrentJobTitle, selectCurrentJobTitleLevel } from './onboarding.store';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { GA_EVENTS } from '../../const.variables';
import { DashboardProxy } from '../dashboard/dashboard.proxy';

export enum ONBOARDING_TALENT_STEPS {
  general = 'general',
  hard = 'hardSkills',
  soft = 'softSkills'
}

export enum ONBOARDING_HM_STEPS {
  general = 'general'
}

export enum ONBOARDING_PARTNER_STEPS {
  general = 'general'
}

@Injectable({ providedIn: 'root' })
export class OnboardingProxy {

  stepsState$ = this.store.select(selectStepsState);
  currentStep$ = this.store.select(selectCurrentSteps);
  jobTitles$ = this.store.select(selectJobTitles);
  userEmail$ = this.store.select(selectUserEmail);
  talentGeneral$ = this.store.select(selectTalentGeneral);
  hmGeneral$ = this.store.select(selectHmGeneral);
  partnerGeneral$ = this.store.select(selectPartnerGeneral);
  hardSkills$ = this.store.select(selectTalentHardSkills);
  talentJobTitle$ = this.store.select(selectCurrentJobTitle);
  talentJobTitleLevel$ = this.store.select(selectCurrentJobTitleLevel);
  selectedTalentHardSkills$ = this.hardSkills$.pipe(
    map(skills => skills ? skills.reduce((filtered, item) => {
      if (item.skills && item.skills.length && item.skills.some(val => val.experience || val.years)) {
        filtered.push({ ...item, skills: item.skills.filter((item: any) => item.experience)});
      }
      return filtered;
    }, []) : [])
  );
  softSkills$ = this.store.select(selectTalentSoftSkills).pipe(
    map(skills => skills ? skills : [])
  );
  defaultSoftSkills$ = this.softSkills$.pipe(
    map(skills => skills.every(item => item.experience === 1))
  )
  submitForm$ = new Subject();
  stepInProgress$ = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store<OnboardingState>,
    private actions$: Actions,
    private router: Router,
    private apiService: ApiService,
    private userProxy: UserProxy,
    private gaService: GoogleAnalyticsService,
    private dashboardProxy: DashboardProxy
  ) {
  }

  initSteps() {
    this.store.dispatch(InitSteps());
  }

  checkServerSteps() {
    this.store.dispatch(CheckServerSteps());
  }

  setOnboardingRole(role) {
    return new Promise((resolve) => {
      this.actions$.pipe(ofType(SetUserRoleSuccess), take(1), tap(async () => {
        this.initSteps();
        let route = '';
        if (role === USER_ROLES.talent) { route = 'talent'; }
        if (role === USER_ROLES.hm) { route = 'hm'; }
        if (role === USER_ROLES.partner) { route = 'partner'; }

        try {
          const res: any = await this.userProxy.getUser();
          if (res && res.redirectTo === 'emailPolicyBreak') {
            this.router.navigate(['email-policy-break']);
            resolve({ redirectTo: 'emailPolicyBreak' });
            return;
          }
        } catch (e) {
          // ignore and continue to onboarding route
        }

        this.router.navigate(['onboarding/' + route]);
        resolve({ redirectTo: 'onboarding', route });
      })).subscribe();
      this.store.dispatch(SetUserRole({ role }));
    });
  }

  setCurrentStep(currentStep: number) {
    this.store.dispatch(SetCurrentStep({ currentStep }));
  }

  getGeneralInfo() {
    this.store.dispatch(GetGeneralInfo());
  }

  checkValidation(key: string) {
    this.submitForm$.next(key);
  }

  validationSuccess(section: string, key: string, data: any = null) {
    this.store.dispatch(StepValid({ section, key, data }));
  }

  async setGeneralInfo(section: string, data: any = null) {
    this.store.dispatch(SetGeneralInfo({ section, data }));
    return await firstValueFrom(this.actions$.pipe(ofType(StepValid)));
  }

  getHardSkills() {
    this.store.dispatch(GetHardSkills());
  }

  setHardSkills(skillsList: any[] = []) {
    this.store.dispatch(SetHardSkills({ skillsList }));
  }

  getSoftSkills() {
    this.store.dispatch(GetSoftSkills());
  }

  async setSoftSkills(skillsList: any[]) {
    setTimeout(() => this.store.dispatch(SetSoftSkills({ skillsList })));
    return await firstValueFrom(this.actions$.pipe(ofType(SetSoftSkillsSuccess)));
  }

  getJobTitles() {
    this.store.dispatch(GetJobTitles());
  }

  async setTalentSeniority(level) {
    setTimeout(() => this.store.dispatch((SetTalentSeniority({ level }))));
    return await firstValueFrom(this.actions$.pipe(ofType(SetTalentSenioritySuccess, SetTalentSeniorityFailure)));
  }

  getSkillsList() {
    this.store.dispatch(GetSkillsList());
  }

  onboardComplete(recalculate = true) {
  return this.apiService.onboardComplete().pipe(
    tap(async (res) => {
      if (recalculate) {
        this.dashboardProxy.recalculateMatch(null, true);
      }
      await this.userProxy.getUser(); 
      this.stepInProgress$.next(false);
      this.userProxy.isOnboarding$.next(false);
      this.gaService.gtag('event', GA_EVENTS.RegistrationComplete);

      const slRedirect = localStorage.getItem('slRedirect');
      if (slRedirect) {
        localStorage.removeItem('slRedirect');
        setTimeout(() => this.router.navigate([slRedirect]), 1000);
      } else {
      // this.router.navigate(['./dashboard'], { queryParams: { onboardComplete: true }});
        const isAccountAdmin = await firstValueFrom(
          this.userProxy.isAccountAdmin$.pipe(take(1))
        );
        this.router.navigate([isAccountAdmin ? 'dashboard/upgrade' : 'dashboard']);
      }

      setTimeout(async () => {
        await this.userProxy.getUser();
        this.userProxy.userFiles$.pipe(take(1)).subscribe(values => {
          if (values && values.length) {
            const cvFile = values.find(file => file.details?.type === 'cv');
            console.log('cvFile', cvFile);
            if (cvFile) {
              this.userProxy.setTalentDefaultCv({ fileUrl: cvFile.fileUrl, type: cvFile.type });
              this.apiService.setDefaultCv(cvFile.fileUrl , cvFile.type).subscribe();
            }
          }
        });
      });
    }),
    catchError(err => {
      this.stepInProgress$.next(false);
      return throwError(() => err);
    })
  );
}

}
